import { SalesReportError } from '@/components/money/SalesReportError'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { LoadingState } from '@/components/patterns/loading-state'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { canDisplaySalesReport } from '@/lib/services/money/money.query'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  reportQueries,
  useAccountingJournalReport,
  useExportAccountingJournalReportMutation,
} from '@/lib/services/reports/reports.query'
import {
  accountingJournalReportSearchSchema,
  getAccountingJournalReportFiltersFromSearch,
  type AccountingJournalReportFilters,
  type AccountingJournalReportSearch,
} from '@/lib/services/reports/reports.schema'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { BookOpenCheck, Download } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { AccountingJournalResults } from './-results'

/** The API reads at most a quarter at a time (`AccountingJournalReportService::MAX_DAYS`). */
const MAX_DAYS = 92

export const Route = createFileRoute('/_authenticated/$orgId/reports/accounting-journal/')({
  component: AccountingJournalPage,
  validateSearch: (search) => accountingJournalReportSearchSchema.parse(search),
  // Only the dates choose the journal; the table pages it in the browser.
  loaderDeps: ({ search: { start_date, end_date, date_mode } }) => ({
    start_date,
    end_date,
    date_mode,
  }),
  loader: async ({ context, deps: search, params }) => {
    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const { filters } = getReportFilters(search, organization?.timezone)

    // An incomplete or failed journal shows inline on the page instead of failing the route. The router reruns this
    // loader on every navigation, paging included, so it only fills an empty cache: recomputing the whole journal is
    // the page query's call, when it mounts or the dates change.
    await context.queryClient.prefetchQuery({
      ...reportQueries.accountingJournal({ organizationId: params.orgId, filters }),
      staleTime: Infinity,
    })
  },
})

export function AccountingJournalPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const { filters, clamped } = getReportFilters(search, organization?.timezone)
  const {
    data: report,
    error,
    isError,
    isPending,
    isFetching,
    refetch,
  } = useAccountingJournalReport(filters, { retryOnMount: false })
  const showReport = canDisplaySalesReport({ data: report, error, isError })
  const exportMutation = useExportAccountingJournalReportMutation()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )

  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) => {
    startTransition(() => {
      void navigate({
        ...path.accountingJournal.index().using({ orgId }).link,
        search: { start_date, end_date, date_mode: date_mode ?? undefined },
        replace: Boolean(search.start_date && search.end_date) || Boolean(search.date_mode),
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({ organizationId: orgId, filters })

      downloadExportedReport(response, `journal-${filters.start_date}-to-${filters.end_date}.xlsx`)
    } catch (exportError) {
      toast.error(await getExportErrorMessage(exportError, 'Failed to export the journal'))
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <AdminPageHeader
        icon={BookOpenCheck}
        title="Journal entries"
        description="The shop's double-entry journal, computed from its sales, payments, payouts and cash drawers. Internal: for checking the books before they reach QuickBooks."
        className="mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={filters.date_mode ?? REPORT_DATE_MODE.CUSTOM}
              initialFilters={{ ...filters, ...datePickerBounds }}
              now={organizationNow}
              onApply={applyFilters}
            />
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              onClick={() => void handleExport()}
              disabled={!showReport || exportMutation.isPending}
            >
              <Download className="h-4 w-4" />
              Export Spreadsheet
            </AdminHeaderActionButton>
          </>
        }
      />

      {clamped && (
        <Typography variant="supporting">
          The journal covers up to {MAX_DAYS} days at a time, so this shows{' '}
          {dayjs(filters.start_date).format('MMM D, YYYY')} to{' '}
          {dayjs(filters.end_date).format('MMM D, YYYY')}.
        </Typography>
      )}
      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          hasCachedReport={showReport}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading the journal…" />
      ) : showReport && report ? (
        <AccountingJournalResults report={report} orgId={orgId} />
      ) : null}
    </div>
  )
}

function getReportFilters(
  search: AccountingJournalReportSearch,
  organizationTimezone: string
): { filters: AccountingJournalReportFilters; clamped: boolean } {
  const { start_date, end_date, date_mode } = getCurrentReportDateFilters(
    getOrganizationNow(organizationTimezone)
  )
  const filters = getAccountingJournalReportFiltersFromSearch(search, {
    start_date,
    end_date,
    date_mode,
  })
  const earliestStart = dayjs(filters.end_date)
    .subtract(MAX_DAYS - 1, 'day')
    .format('YYYY-MM-DD')

  return filters.start_date < earliestStart
    ? { filters: { ...filters, start_date: earliestStart }, clamped: true }
    : { filters, clamped: false }
}
